import type { Component } from 'vue'
import {
  CheckIcon,
  ClockCircleIcon,
  CloseIcon,
  DangerTriangleIcon,
  PenIcon,
} from '@solar-icons/vue/linear'
import { LinkedinIcon, WhatsappIcon } from '@/components/primitives/brand-icons'
import type { PendingAction, PendingConnect, PendingQuestion } from '../../types/chat'
import type { SocialPlatform } from '../../types/connections'

/** Looks a message up. Narrower than vue-i18n's own `t`, so these stay plain functions. */
export type AgentAskTranslate = (key: string, named?: Record<string, unknown>) => string

/** One button in an ask's action row. The first choice is always the primary one. */
interface AgentAskChoice {
  /** Echoed back through the card's single `choose` event. */
  readonly id: string
  readonly label: string
  readonly variant: 'primary' | 'secondary' | 'ghost'
  readonly icon?: Component
  /** Shows the spinner on this one choice. At most one choice may be busy. */
  readonly busy?: boolean
}

/** The body a mode draws instead of a prompt sentence - a logo, a name and a caption. */
interface AgentAskIdentity {
  readonly icon: Component
  /** Token classes tinting the logo tile. */
  readonly toneClass: string
  readonly name: string
  readonly caption: string
}

/** The change itself, shown in the card where the pending input already holds the new text. */
interface AgentAskPreview {
  readonly before: string
  readonly after: string
}

/** What replaces the whole body once the ask is resolved. */
interface AgentAskSettled {
  readonly tone: 'positive' | 'neutral' | 'negative'
  readonly icon: Component
  readonly message: string
  /** The `sr-only` line announcing that the ask is over. */
  readonly announcement: string
}

/** Everything `AgentAsk` draws. It knows nothing beyond this. */
export interface AgentAskModel {
  /** Body when the ask is open and speaks in a sentence. */
  readonly prompt?: string
  /** Body when the mode shows an identity instead of a sentence. Set in place of `prompt`. */
  readonly identity?: AgentAskIdentity
  /** Shown under `prompt` when the pending input holds the change in full. */
  readonly preview?: AgentAskPreview
  readonly choices: readonly AgentAskChoice[]
  /** Quiet line under the buttons - the undo contract. */
  readonly footnote?: string
  /** Red line under the body. A live failure, not a settled outcome. */
  readonly error?: string
  /** Set once the ask is resolved. Replaces prompt, identity, choices and footnote. */
  readonly settled?: AgentAskSettled
}

/** The choice ids `askFromAction` emits. */
export const ACTION_CHOICE = { confirm: 'confirm', decline: 'decline' } as const

/** The choice ids `askFromConnect` emits. */
export const CONNECT_CHOICE = { connect: 'connect', skip: 'skip' } as const

/** The choice id `askFromQuestion` emits for the free-text escape hatch. */
export const QUESTION_FREE_TEXT_CHOICE = 'free-text'

/** Builds the id carrying a question option's position, so duplicate labels stay distinct. */
function questionOptionChoiceId(index: number): string {
  return `option:${index}`
}

/** Reads an option's position back out of a choice id, or `null` when the id is not an option. */
export function questionOptionIndex(choiceId: string): number | null {
  if (!choiceId.startsWith('option:')) return null
  const index = Number.parseInt(choiceId.slice('option:'.length), 10)
  return Number.isInteger(index) && index >= 0 ? index : null
}

/** The one tool whose pending input already holds the finished text. */
const PREVIEWABLE_TOOL = 'editPostBody'

/** Resolves the text a pending change would replace - the post's current body, by id. */
export type AgentAskResolveBody = (postId: string) => string | undefined

function previewFromAction(
  action: PendingAction,
  resolveBody: AgentAskResolveBody | undefined,
): AgentAskPreview | undefined {
  if (!resolveBody || action.toolName !== PREVIEWABLE_TOOL) return undefined

  const { postId, body } = action.input
  if (typeof postId !== 'string' || typeof body !== 'string') return undefined

  const before = resolveBody(postId)
  if (before === undefined || before === body) return undefined

  return { before, after: body }
}

export function askFromAction(
  action: PendingAction,
  t: AgentAskTranslate,
  resolveBody?: AgentAskResolveBody,
): AgentAskModel {
  const announcement = t('chat.agentAsk.action.settled')

  if (action.status === 'executed') {
    return action.failureReason !== undefined
      ? {
          choices: [],
          settled: {
            tone: 'negative',
            icon: DangerTriangleIcon,
            message: t('chat.agentAsk.action.failedNote', { reason: action.failureReason }),
            announcement,
          },
        }
      : {
          choices: [],
          settled: {
            tone: 'positive',
            icon: CheckIcon,
            message: t('chat.agentAsk.action.done'),
            announcement,
          },
        }
  }

  if (action.status === 'declined') {
    return {
      choices: [],
      settled: {
        tone: 'neutral',
        icon: CloseIcon,
        message: t('chat.agentAsk.action.declined'),
        announcement,
      },
    }
  }

  if (action.status === 'expired') {
    return {
      choices: [],
      settled: {
        tone: 'neutral',
        icon: ClockCircleIcon,
        message: t('chat.agentAsk.action.expired'),
        announcement,
      },
    }
  }

  const preview = previewFromAction(action, resolveBody)

  return {
    prompt: action.summary,
    ...(preview ? { preview } : {}),
    choices: [
      { id: ACTION_CHOICE.confirm, label: t('chat.agentAsk.action.allow'), variant: 'primary' },
      { id: ACTION_CHOICE.decline, label: t('chat.agentAsk.action.notNow'), variant: 'ghost' },
    ],
    footnote: undoFootnote(action, t),
  }
}

function undoFootnote(action: PendingAction, t: AgentAskTranslate): string | undefined {
  if (action.undoNote) return action.undoNote
  if (action.undoable === false) return t('chat.agentAsk.action.cannotUndo')
  return undefined
}

export function askFromQuestion(question: PendingQuestion, t: AgentAskTranslate): AgentAskModel {
  const announcement = t('chat.agentAsk.question.settled')

  if (question.status === 'answered') {
    return {
      choices: [],
      settled: {
        tone: 'positive',
        icon: CheckIcon,
        message: t('chat.agentAsk.question.answered'),
        announcement,
      },
    }
  }

  if (question.status === 'expired') {
    return {
      choices: [],
      settled: {
        tone: 'neutral',
        icon: ClockCircleIcon,
        message: t('chat.agentAsk.question.expired'),
        announcement,
      },
    }
  }

  const options: AgentAskChoice[] = question.options.map((option, index) => ({
    id: questionOptionChoiceId(index),
    label: option,
    variant: index === 0 ? 'primary' : 'secondary',
  }))

  if (question.allowFreeText) {
    options.push({
      id: QUESTION_FREE_TEXT_CHOICE,
      label: t('chat.agentAsk.question.typeAnswer'),
      variant: 'ghost',
      icon: PenIcon,
    })
  }

  return { prompt: question.question, choices: options }
}

// Third-party product names, not UI copy - never translated.
const PLATFORM_NAMES: Record<SocialPlatform, string> = {
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
}

const PLATFORM_ICONS: Record<SocialPlatform, Component> = {
  linkedin: LinkedinIcon,
  whatsapp: WhatsappIcon,
}

const PLATFORM_TONE_CLASSES: Record<SocialPlatform, string> = {
  linkedin: 'bg-platform-linkedin/10 text-platform-linkedin',
  whatsapp: 'bg-platform-whatsapp/10 text-platform-whatsapp',
}

/** The live connection state the connect mode reads. Passed in, so this stays a pure function. */
export interface ConnectProgress {
  /** True while the OAuth handoff is being opened. */
  readonly connecting: boolean
  /** The last connect failure, when there is one. */
  readonly error?: string
}

export function askFromConnect(
  connect: PendingConnect,
  progress: ConnectProgress,
  t: AgentAskTranslate,
): AgentAskModel {
  const platformName = PLATFORM_NAMES[connect.platform]
  const announcement = t('chat.agentAsk.connect.settled')

  if (connect.status === 'completed') {
    return {
      choices: [],
      settled: {
        tone: 'positive',
        icon: CheckIcon,
        message: t('chat.agentAsk.connect.connected', { platform: platformName }),
        announcement,
      },
    }
  }

  if (connect.status === 'expired') {
    return {
      choices: [],
      settled: {
        tone: 'neutral',
        icon: ClockCircleIcon,
        message: t('chat.agentAsk.connect.expired'),
        announcement,
      },
    }
  }

  return {
    identity: {
      icon: PLATFORM_ICONS[connect.platform],
      toneClass: PLATFORM_TONE_CLASSES[connect.platform],
      name: platformName,
      caption: t('chat.agentAsk.connect.caption'),
    },
    choices: [
      {
        id: CONNECT_CHOICE.connect,
        label: t('chat.agentAsk.connect.connect', { platform: platformName }),
        variant: 'primary',
        busy: progress.connecting,
      },
      { id: CONNECT_CHOICE.skip, label: t('chat.agentAsk.connect.skip'), variant: 'ghost' },
    ],
    error: progress.error,
  }
}
